/**
 * Hand-written mirrors of the saltseed REST DTOs.
 *
 * Authoritative source: ARCHITECTURE.md §16 (zones), §17 (plans and lifecycle)
 * and §18 (the REST surface). Where §18 leaves a payload underspecified the
 * shape assumed here is called out in a comment tagged `ASSUMED`.
 *
 * Field naming follows the contract: the wire format is snake_case, matching
 * both the `queued_messages` payload spelled out in §5 and the snake_case
 * persistence convention in §10. Do not "fix" these to camelCase.
 */

/* -------------------------------------------------------------------------- */
/* Enumerations                                                               */
/* -------------------------------------------------------------------------- */

/**
 * §17. One enum shared by `accounts.account_state` and `zones.zone_state`.
 * `ARCHIVED` is terminal — there is no path back and no DELETE at any layer.
 */
export type LifecycleState = 'ACTIVE' | 'DEACTIVATED' | 'ARCHIVED'

/** §16. `memberships.member_role`. Was OWNER | MEMBER before zones. */
export type MembershipRole = 'ADMIN' | 'USER' | 'BILLING'

/**
 * §16. `connections.type`, lowercase on the wire (`ConnectionType.wireValue()`).
 * A HUMAN is a first-class named connection driven from this SPA.
 */
export type ConnectionType = 'agent' | 'human' | 'police'

/**
 * `connections.connection_state`. Uppercase on the wire.
 *
 * IMPROVEMENTS_1 (WI1, WI9) made the name durable. A `DISCONNECTED` row the connection list still
 * returns is a **reserved identity that is offline**: it keeps its name, its queue and its channel
 * memberships, and comes back as itself when its client reconnects with its connection id. A
 * `RELEASED` row exists in the database (its history is kept under `name#N`) but is never listed,
 * so the SPA never sees that value.
 */
export type ConnectionState = 'CONNECTED' | 'DISCONNECTED'

/**
 * `messages.message_state`. Rows are never deleted, only moved between these.
 *
 * §19 added `HELD`: a message written while either end of it was paused. It is
 * neither delivered nor lost — `read`/`peek`/`pop`/`count` skip it entirely —
 * and the zone administrator decides at unpause time whether it becomes
 * `QUEUED` (DELIVER) or `DISCARDED` (DISCARD).
 *
 * IMPROVEMENTS_1 (WI5) added `EXPIRED`: the sender gave it a `ttl_seconds` that ran out before the
 * recipient read it. Never delivered, never counted, still in the history.
 */
export type MessageState = 'QUEUED' | 'HELD' | 'CONSUMED' | 'DISCARDED' | 'EXPIRED'

/**
 * `messages.source_type`, lowercase on the wire.
 *
 * Deliberately still `session` after §16 renamed sessions to connections: the
 * §5/§6 message payload is the published MCP API and renaming it would break
 * every existing client. Read `session` as "a single connection, not a channel".
 */
export type MessageSourceType = 'session' | 'channel'

/** `GET /api/zones?state=` — §17: archived zones are hidden unless asked for. */
export type ZoneStateFilter = 'active' | 'all' | 'archived'

/* -------------------------------------------------------------------------- */
/* Plans and limits (§17)                                                     */
/* -------------------------------------------------------------------------- */

/** A `plans` row. `GET /api/plans` returns the catalogue, for display only. */
export interface Plan {
  id: string
  /** `free` | `solo` | `team` (stripe.md §4) — extensible, so typed as a string. */
  code: string
  name: string
  /** Ordering key, low to high. */
  rank: number
  max_zones: number
  max_connections_per_zone: number
  max_channels_per_zone: number
  max_members: number
}

/**
 * The four quotas actually in force for an account:
 * `coalesce(accounts.<limit>_override, plan.<limit>)` (§17). Null never appears
 * here — the coalesce happens server-side.
 */
export interface EffectiveLimits {
  max_zones: number
  max_connections_per_zone: number
  max_channels_per_zone: number
  max_members: number
}

/* -------------------------------------------------------------------------- */
/* Registration                                                               */
/* -------------------------------------------------------------------------- */

/** `POST /api/auth/register` body. Responds 202 with no useful body. */
export interface RegisterRequest {
  email: string
}

/** `POST /api/auth/verify` body. `token` comes from the emailed link. */
export interface VerifyRequest {
  token: string
  name: string
  password: string
}

/**
 * `POST /api/auth/verify` response.
 *
 * This is the first and (unless rotated) only time the raw bearer token is ever
 * returned. `mcp_url` is the URL of the `main` zone created alongside the
 * account (§16) — the token itself is the user's and spans every zone.
 */
export interface VerifyResponse {
  /**
   * Raw bearer token, format `msk_<43 chars base64url>`. Shown once. **Absent for a
   * BILLING invitee**, who has no zone and no MCP access and so never holds one (BI5).
   */
  token?: string
  /**
   * `https://<env>.saltseed.com/mcp/<zone-public-id>/` for the `main` zone.
   *
   * **Absent — the key is omitted, not null — when the redemption was an
   * invitation.** An invited USER joins an account that already exists, gets no
   * `main` zone of their own, and reaches nothing until an ADMIN grants them a
   * zone. `VerifyResponse` is `@JsonInclude(NON_NULL)` on the server for exactly
   * this, so "no zone yet" is distinguishable from a URL that failed to build.
   */
  mcp_url?: string
  account: AccountSummary
  user: UserSummary
  /** The invitee's role in the account they joined; absent for a self-registration. */
  role?: MembershipRole
}

/** `POST /api/auth/invitation` — what a live emailed link is for, read without redeeming it. */
export interface InvitationPreview {
  invited: boolean
  /** The name the inviting ADMIN typed; pre-fills the accept form. */
  name?: string
  account_name?: string
  role?: MembershipRole
}

/* -------------------------------------------------------------------------- */
/* Identity                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A `users` row as the SPA sees it. Since §16 the bearer token belongs to the
 * user, so `token_prefix` lives here rather than on the account.
 */
export interface UserSummary {
  id: string
  email: string
  name: string
  email_verified: boolean
  /** First 8 chars of the raw token (`msk_` + 4), for display only; null for a billing-only member. */
  token_prefix: string | null
  created_at: string
}

/**
 * An `accounts` row, its plan, the limits actually in force and its state.
 * `GET /api/account` — §18 "account + plan + effective limits + state".
 *
 * `plan` and `limits` are nested objects rather than flattened `plan_code` /
 * `max_*` fields — the three `max_*` columns that used to sit on `accounts` are
 * gone (§17), so nothing here is flat any more. Verified against
 * `AccountSummaryDto`.
 */
export interface AccountSummary {
  id: string
  name: string
  slug: string
  account_state: LifecycleState
  /** When `account_state` last changed. */
  state_changed_at: string
  plan: Plan
  limits: EffectiveLimits
  /**
   * How long an unread agent secret lives, in seconds (D30). Default 3600;
   * bounds 60..86400, enforced server-side.
   */
  secret_lifetime_seconds: number

  /**
   * Connection presence (D32). How long, in seconds, an agent's connection may go
   * without a tool call before the presence sweep marks it disconnected. Default
   * 1800; an admin may set 60..86400. Lease-based: any authenticated call refreshes it.
   */
  presence_idle_seconds: number
  /**
   * Agent file transfers (D31). `max_upload_bytes` is the EFFECTIVE limit —
   * `min(account override, plan)` — and `plan_max_upload_bytes` the ceiling an
   * override may not exceed. `blocked_mime_types` are `type/subtype` or
   * `type/*` patterns matched against the DETECTED type of an upload.
   * `uploads_enabled` is false when the server has no bucket configured; the
   * console then hides the Uploads page and the tools answer "not enabled".
   */
  max_upload_bytes: number
  plan_max_upload_bytes: number
  blocked_mime_types: string[]
  uploads_enabled: boolean
  created_at: string
}

/**
 * One passed secret, as the console may see it — `GET /api/zones/{zoneId}/secrets`.
 *
 * There is NO value field, and no route that could return one (D30). This is
 * the record that a secret was passed: who, to whom, what name, when, how it
 * ended.
 */
export interface SecretEvent {
  id: string
  name: string
  from: string
  to: string
  sent_at: string
  resolved_at: string | null
  outcome: 'SENT' | 'READ' | 'EXPIRED' | 'DELETED'
}

/* -------------------------------------------------------------------------- */
/* Agent file transfers (D31)                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Where an upload is in its life. `PENDING` = handle issued, bytes not yet
 * received; `STORED` = in the bucket; `REJECTED`/`FAILED`/`QUARANTINED` = the
 * bytes never became a file recipients can fetch (`reject_reason` says why);
 * `EXPIRED` = never uploaded in time; `DELETED` = removed by an administrator
 * (`deleted_by`, `deleted_at`) or by retention.
 */
export type UploadStatus =
  | 'PENDING'
  | 'STORED'
  | 'REJECTED'
  | 'FAILED'
  | 'QUARANTINED'
  | 'EXPIRED'
  | 'DELETED'

/** One party the sender addressed. Redemption is per recipient (D31 decision 10). */
/** One secure file waiting for the signed-in person (SECURE_FILES decision 10). */
export interface SecureFileDto {
  handle: string
  filename: string
  content_type: string | null
  size_bytes: number
  sender: string
  stored_at: string
  /** When it is destroyed unclaimed. */
  expires_at: string
}

export interface UploadRecipient {
  name: string
  redeemed_at: string | null
  download_attempts: number
}

/**
 * One upload, as the console may see it — `GET /api/zones/{zoneId}/uploads`.
 *
 * There is NO route that returns the bytes to a browser: files are for agents,
 * and the console records the exchange. `filename` is caller-supplied text and
 * is rendered as text, never as markup.
 */
export interface UploadDto {
  handle: string
  filename: string
  declared_content_type: string
  detected_content_type: string | null
  size_bytes: number
  sender: string
  recipients: UploadRecipient[]
  status: UploadStatus
  allow_multiple_downloads: boolean
  created_at: string
  stored_at: string | null
  reject_reason: string | null
  deleted_at: string | null
  /** Display name of the administrator who removed the file; never a user id. */
  deleted_by: string | null
  /**
   * SECURE_FILES: the contents were a credential — exactly one recipient, encrypted with the
   * zone's key, destroyed on its first completed download, and destroyed unclaimed at
   * `secret_expires_at`. The console marks the row and shows no more of it than of any other
   * file, which is nothing: there is no URL, token or key anywhere in this shape.
   */
  is_secret: boolean
  /** When an unclaimed secure file is destroyed. Null for an ordinary file, and once it is gone. */
  secret_expires_at: string | null
}

/**
 * `GET /api/zones/{zoneId}/uploads?page&size` — newest first.
 *
 * <p>The server answers a **bare array** (`UploadsController.list` returns `List<UploadDto>`), not
 * an envelope. This type used to declare `{ items, has_more }`, so every read of `data.items` was
 * `undefined` and the page showed "No files have been passed in this zone" however many there were
 * — bug-178. It went unseen because local dev runs with uploads disabled, so the table was
 * legitimately empty on every machine that looked at it.
 *
 * <p>"Is there more" is derived from the page size rather than sent: a full page means there may be
 * another, which is all the sentence under the table claims.
 */
export type UploadsPage = UploadDto[]

/** One download attempt — `GET /api/zones/{zoneId}/uploads/{handle}/downloads`. */
export interface DownloadEventDto {
  recipient: string
  started_at: string
  completed_at: string | null
  bytes_served: number
  outcome: 'COMPLETED' | 'ABORTED'
}

/** `GET /api/me` — §18 `{user, account, role, plan, zones[]}`. */
export interface MeResponse {
  user: UserSummary
  account: AccountSummary
  role: MembershipRole
  /** 2026-10-BILLING-IN-APP (BI5): effective billing access: ADMIN, the BILLING role, or a USER with the flag. */
  billing: boolean
  plan: Plan
  /** Every zone this user may reach, `ACTIVE` and `DEACTIVATED`. */
  zones: ZoneSummary[]
  /** Holds the platform-wide `saltseed-staff` realm role: sees the feedback inbox. */
  staff: boolean
  /** The staff tier (`support` < `operator` < `superoperator` < `superadmin`), or null. */
  staff_tier?: string | null
  /** False for staff whose session lacks the second factor: every staff endpoint answers `mfa_required`. */
  staff_mfa?: boolean
  /** Present only under `X-Saltseed-View-As`. */
  view_as?: {
    account_id: string
    account_name: string
    impersonation_id: string
    expires_at: string
  } | null
}

/** `GET /api/account` */
export type AccountResponse = AccountSummary

/**
 * `POST /api/me/token/rotate`
 *
 * The previous token is invalidated server-side; `token` is shown once. No
 * `mcp_url`: the token spans every zone the user can reach, so there is no one
 * URL it belongs to.
 */
export interface RotateTokenResponse {
  token: string
  token_prefix: string
}

/* -------------------------------------------------------------------------- */
/* Zones (§16, §17)                                                           */
/* -------------------------------------------------------------------------- */

/** A `zones` row as the SPA sees it, with the MCP URL it is reachable at. */
export interface ZoneSummary {
  /** The UUID. Every `/api/zones/{zoneId}` path segment uses this. */
  id: string
  /** The opaque `<zone-id>` in `/mcp/<zone-id>/`. Stable across renames. */
  public_id: string
  name: string
  description: string | null
  /** `https://<env>.saltseed.com/mcp/<public_id>/` */
  mcp_url: string
  zone_state: LifecycleState
  /**
   * Whether anything can actually be written into this zone right now.
   *
   * **Not the same as `zone_state === 'ACTIVE'`**, and this is the field to
   * branch on. §17: "no zone of a non-`ACTIVE` account is reachable, whatever
   * the zone's own state" — so an ACTIVE zone of a DEACTIVATED account has
   * `zone_state: 'ACTIVE'` and `reachable: false`. The server folds both halves
   * of the rule together here (`ZoneService.isReachable`) so the console never
   * has to reimplement the cascade from two separate states.
   */
  reachable: boolean
  /**
   * §19's third authority: the user who administers this zone, defaulting to
   * whoever created it.
   *
   * Null when nobody holds the seat — authority then reverts to the account's
   * ADMINs, which is also who the pause mail goes to. Only an account ADMIN can
   * change it (`PATCH /api/zones/{zoneId}`), because a zone administrator who
   * could appoint their own successor could hand the zone away.
   */
  administrator_user_id: string | null
  /** When `zone_state` last changed. */
  state_changed_at: string
  created_at: string
  /**
   * IMPROVEMENTS_1 (WI6): how often an agent in this zone is told to check for messages, in
   * seconds — `idle` between conversations, `active` while one is going. Defaults 30 and 10;
   * bounds 5…3600 with active ≤ idle, enforced server-side. Agents read them from `connect` and
   * `refresh`, so a change reaches them on their next call.
   */
  poll_idle_seconds: number
  poll_active_seconds: number
  /**
   * CONNECTION_BUGS decision 4 (CB-WI3): the two offline timers, in seconds and in days.
   *
   * `offline_after_seconds` is how long an agent may be silent before it shows as offline —
   * nothing is lost when it passes, the name stays reserved and the queue keeps filling. Defaults
   * 7200; bounds 60…86400. `offline_release_days` is how long an offline identity then keeps its
   * name before it is released automatically; default 10, bounds 0…365, and **0 means never**.
   */
  offline_after_seconds: number
  offline_release_days: number
  /**
   * SECURE_FILES decisions 5-6: how long an unclaimed **secure file** lives in this zone, in
   * seconds. Default 3600, bounds 60…86400 — the same shape and bounds as the account's secret
   * lifetime, so an administrator meets one rule and not two. The clock starts when the bytes are
   * stored, not when the offer is made, so a slow upload cannot eat the recipient's window.
   */
  secure_file_ttl_seconds: number
}

/** `POST /api/zones` body (ADMIN). */
export interface CreateZoneRequest {
  name: string
  description?: string
}

/**
 * `PATCH /api/zones/{zoneId}` body (ADMIN). Every field optional and
 * independent; an absent one is left alone.
 *
 * `administrator_user_id` is a **string, not a UUID type**, and the three cases
 * are genuinely distinct on the server (`UpdateZoneRequest` carries it as a
 * `String` for exactly this reason):
 *
 *  - absent / `undefined` — leave the current administrator alone
 *  - `''` — **clear** the seat; authority falls back to the account's ADMINs
 *  - a UUID — appoint that user, who must be a member of the zone's account
 *    (404 otherwise, never 403 — the same non-oracle rule as everywhere else)
 */
export interface UpdateZoneRequest {
  name?: string
  description?: string
  administrator_user_id?: string
  /**
   * WI6 cadence, either alone or both. 5…3600 and active ≤ idle; anything else is a 400 problem
   * titled "Poll cadence out of range", which the settings form shows verbatim.
   */
  poll_idle_seconds?: number
  poll_active_seconds?: number
  /**
   * CB-WI3 offline timers, either alone or both. 60…86400 seconds and 0…365 days; anything else
   * is a 400 problem titled "Offline timers out of range", which the form shows verbatim.
   */
  offline_after_seconds?: number
  offline_release_days?: number
  /**
   * SECURE_FILES: 60…86400. Anything else is a 400 problem the form shows verbatim, like the
   * other two groups.
   */
  secure_file_ttl_seconds?: number
}

/**
 * `GET /api/zones/{zoneId}/members` row (ADMIN).
 *
 * ASSUMED shape. The user id is the only load-bearing field — it is the
 * `{userId}` of `PUT`/`DELETE .../members/{userId}` — the rest is for display
 * and is cross-referenced against `GET /api/users` by the console anyway.
 */
export interface ZoneMemberSummary {
  user_id: string
  email: string
  name: string
  granted_at: string
}

/* -------------------------------------------------------------------------- */
/* Account users (§18)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * `GET /api/users` row (ADMIN): a user of this account plus their membership
 * role and their state.
 *
 * ASSUMED: `user_state`. §17 defines `LifecycleState` for accounts and zones
 * only, but §18 has `POST /api/users/{userId}/deactivate|activate`, so a user
 * must carry one too. `user_state` follows the §10 prefixing convention
 * (`account_state`, `zone_state`, `connection_state`, `message_state`).
 */
export interface AccountUser {
  id: string
  email: string
  name: string
  email_verified: boolean
  role: MembershipRole
  /** Effective billing access: ADMIN, BILLING, or a USER with the flag. */
  billing: boolean
  user_state: LifecycleState
  created_at: string
}

/** `POST /api/users` body (ADMIN) — sends an invitation mail. */
export interface InviteUserRequest {
  email: string
  name: string
  role: MembershipRole
  /**
   * Zones the invitee should reach immediately (§16). Optional; omitted or `[]` grants none, which
   * is what a USER got before this existed. **Ignored for an ADMIN** — they reach every zone from
   * the role, so the server writes no rows and the form does not offer the choice.
   */
  zone_ids?: string[]
  /** The USER billing flag (BI5); ignored for ADMIN and BILLING. */
  billing?: boolean
}

/** `PATCH /api/users/{userId}` body (ADMIN). */
export interface UpdateUserRequest {
  role: MembershipRole
  /** The USER billing flag; absent keeps it. */
  billing?: boolean
}

/* -------------------------------------------------------------------------- */
/* Connections (§16)                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A `connections` row as the SPA sees it. `GET /api/zones/{zoneId}/connections`
 *
 * The list carries two kinds of row since IMPROVEMENTS_1: everything `CONNECTED` (agents and
 * humans), and every **reserved identity that is offline** — an agent row with
 * `state: 'DISCONNECTED'` that keeps its name and its queue until it reconnects with its id or is
 * released. Humans who left are not listed, and released rows never are. So "DISCONNECTED in this
 * list" means "not here now", never "gone".
 */
export interface ConnectionSummary {
  id: string
  /** Claimed name, unique per zone among non-released rows. Renders in mono. */
  name: string
  type: ConnectionType
  state: ConnectionState
  /**
   * CB-WI5. Restates `created_at` under the name the `list` MCP tool uses, so the console and an
   * agent describe the same fact identically: when THIS wearer of the name claimed it. A name is
   * given back by `release` and worn by the next claimant, so this is not "when the row was
   * created" in the abstract — it is "since when has this been the current holder".
   */
  claimed_at: string
  /**
   * CB-WI5. How many times this name has been claimed, 1 for the first holder. **Null** on the
   * single-row replies (pause, unpause, presence) that answer about one row the caller already
   * named rather than list a zone's connections — those have no repository call to count it with.
   * The zone connection listing always carries a number.
   */
  incarnation: number | null
  /**
   * §19. **Not a value of `state`** — a paused connection stays `CONNECTED` and
   * its tools keep working; only delivery stops, in both directions. Never infer
   * this from `state`, which never says it.
   */
  paused: boolean
  /** Null unless `paused`. */
  paused_at: string | null
  /**
   * The police connection that ordered it, or null when a human paused it from
   * the console. Ids, not names: the server refuses to walk the lazy relation
   * during serialisation (bug-010), so the console resolves the name against the
   * connection list it already holds.
   */
  paused_by_connection_id: string | null
  /** The user who ordered or authorised it. Null for a purely machine pause. */
  paused_by_user_id: string | null
  /** Free text, delivered verbatim to the agent and mailed to the administrator. */
  pause_reason: string | null
  created_at: string
  last_activity_at: string
  /** Null while CONNECTED. For an offline identity this is when it was last seen. */
  disconnected_at: string | null
  /**
   * Whether THIS viewer may reissue the row's connection id: its owner, or the zone administrator.
   * Per caller, so two people looking at the same zone can see different values.
   */
  can_reissue: boolean
  /**
   * Protocol 7, A4. What the server sees of this identity's waste: empty `receive(wait_seconds>0)`
   * calls (total and in a row), receives that overlapped a running one, and reclaims by id.
   * Optional so an older server's reply still types; absent means "not counted".
   */
  counters?: ConnectionCounters
}

export interface ConnectionCounters {
  empty_waits: number
  empty_wait_streak: number
  duplicate_waits: number
  reconnects: number
}

/** `POST /api/zones/{zoneId}/connections/{id}/pause` body (zone administrator). */
export interface PauseConnectionRequest {
  /** Required and non-blank — the server answers 400 for a blank one. */
  reason: string
}

/**
 * `POST /api/zones/{zoneId}/connections/{id}/reissue` response: a new connection id, shown ONCE.
 *
 * The server keeps only a digest of the id, so this response is the only place `connection` ever
 * appears. The old id and the old watch URL stop working the moment this answers. Keep it out of
 * logs, caches and query data: it is a password.
 */
export interface ReissueResponse {
  id: string
  name: string
  /** The raw connection id (`cx_...`). An agent passes it as `connect(connection=...)`. */
  connection: string
  /** Derived from the new id; the previous watch URL is dead. Null for a row that cannot be watched. */
  watch_url: string | null
}

/**
 * `POST /api/zones/{zoneId}/connections/{id}/release` response (zone administrator, WI9).
 *
 * Release is the destructive one, and it is not disconnect. Disconnect takes an agent offline and
 * keeps its name and queue; release frees the name for someone else, discards the unread queue,
 * kills the identity's credentials, and keeps the history under `history_name` (`name#N`). The
 * server answers 404 for anything that is not a releasable identity of this zone — a human,
 * an already-released row, an unknown id.
 */
export interface ReleaseResponse {
  id: string
  /** What the identity's history rows now carry: `name#N`. */
  history_name: string
}

/**
 * §19's two independent choices. `DELIVER` moves the `HELD` backlog to `QUEUED`;
 * `DISCARD` moves it to `DISCARDED`. Nothing is ever deleted either way.
 */
export type HeldMessageDisposition = 'DELIVER' | 'DISCARD'

/**
 * `POST /api/zones/{zoneId}/connections/{id}/unpause` body.
 *
 * **Both fields are required** and there is no server-side default: §19 makes
 * "release what it was told" and "release what it said" two separate decisions,
 * and a default would silently make one of them on the administrator's behalf.
 * A missing or misspelled value is 400, naming the offending field.
 */
export interface UnpauseConnectionRequest {
  inbound: HeldMessageDisposition
  outbound: HeldMessageDisposition
}

/** `POST .../unpause` response. */
export interface UnpauseResponse {
  connection: ConnectionSummary
  /** False when it was not paused to begin with — a 200, not a 409. */
  was_paused: boolean
  inbound: HeldMessageDisposition
  outbound: HeldMessageDisposition
  inbound_released: number
  outbound_released: number
  total_released: number
}

/** `POST /api/zones/{zoneId}/presence` body. Omit `name` to let the server pick. */
export interface JoinZoneRequest {
  name?: string
}

/* -------------------------------------------------------------------------- */
/* Channels                                                                   */
/* -------------------------------------------------------------------------- */

/** A `channels` row plus its member count. `GET /api/zones/{zoneId}/channels` */
export interface ChannelSummary {
  id: string
  name: string
  /** Name of the connection that ran `create_channel`. */
  created_by_connection_name: string | null
  member_count: number
  created_at: string
}

/** `POST /api/zones/{zoneId}/channels` body. Created by my HUMAN connection. */
export interface CreateChannelRequest {
  name: string
}

/**
 * One joined member of a channel — `GET /api/zones/{zoneId}/channels/{channelId}/members` row.
 *
 * **There is no `connection_state` field.** A membership row cannot outlive its connection — the
 * member is removed the moment the connection disconnects — so the value could only ever have been
 * `"CONNECTED"`. It was deliberately left off rather than shipped as a constant.
 */
export interface ChannelMember {
  name: string
  connection_type: ConnectionType
  joined_at: string
}

/* -------------------------------------------------------------------------- */
/* Messaging                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The payload returned by the MCP `read` / `pop` / `peek` tools, and by
 * `GET /api/zones/{zoneId}/inbox`. Exact field order, fixed by §5.
 */
export interface QueuedMessage {
  source_type: MessageSourceType
  source_name: string
  sender: string
  message: string
  /** ISO-8601 instant, UTC. */
  sent_at: string
}

/**
 * `GET /api/zones/{zoneId}/messages` row — the zone's permanent history,
 * newest last.
 *
 * ASSUMED shape: the §5 queue payload plus the four columns that only the
 * history view needs (`id`, `seq`, `state`, `recipient`). `seq` is the
 * database-assigned total order and is the sort key; `recipient` is the name of
 * the connection the row was delivered to, since a channel broadcast writes one
 * row per member (§15).
 */
export interface ZoneMessage {
  id: string
  seq: number
  /**
   * The one thing that was said. Every row of a channel broadcast carries the same value.
   *
   * Grouping used to be inferred from `sent_at`, which the database stamps PER ROW — so two rows of
   * one broadcast differed by microseconds and never grouped, and every broadcast was displayed
   * once per recipient. The group is now stated by the server.
   */
  fanout_id: string
  source_type: MessageSourceType
  source_name: string
  sender: string
  recipient: string
  message: string
  state: MessageState
  sent_at: string
  consumed_at: string | null
  /**
   * WI4: the first time the recipient read or peeked it. Set once and never cleared; the state
   * stays `QUEUED` after a peek, so this is the only way to tell "seen" from "untouched".
   */
  first_read_at: string | null
}

/**
 * `GET /api/zones/{zoneId}/messages` — ONE PAGE of history, oldest first within the page.
 *
 * Keyset paginated on `seq`. The endpoint used to return every row a zone had ever held, which a
 * long conversation turns into a very large response — and a channel broadcast writes one row per
 * member, so the row count grows faster than the conversation does.
 */
export interface ZoneHistoryPage {
  items: ZoneMessage[]
  /** Cursor for the next OLDER page. Null once the beginning is reached. */
  next_before: number | null
  /** Cursor for the next NEWER page. */
  next_after: number | null
  has_more: boolean
}

/** One entry in the jump rail: who spoke, where and when — never the body. */
export interface ZoneMessageIndexEntry {
  fanout_id: string
  /** Lowest seq of the fan-out; the anchor the history window re-centres on. */
  seq: number
  sender: string
  source_type: MessageSourceType
  source_name: string
  sent_at: string
}

/**
 * `GET /api/zones/{zoneId}/messages/index` — the jump rail.
 *
 * `truncated` matters: the index is capped, because it is still one row per message and letting it
 * grow unbounded would move the problem rather than remove it. The rail says so rather than
 * implying the conversation starts where the index does.
 */
export interface ZoneMessageIndex {
  items: ZoneMessageIndexEntry[]
  total: number
  truncated: boolean
}

/* -------------------------------------------------------------------------- */
/* Managed-police billing (D27)                                                */
/* -------------------------------------------------------------------------- */

/**
 * `GET /api/account/balance`.
 *
 * Amounts are **integer micros** — millionths of a currency unit — end to end, and the console must
 * keep them that way. Money in a float is a bug that only shows up once it has been quoted to a
 * customer.
 */
export interface AccountBalance {
  balance_micros: number
  currency: string
  /** The server's floor, so the client states it rather than discovering it in a 422. */
  minimum_topup_micros: number
}

export type BalanceTransactionKind = 'TOPUP' | 'CHARGE' | 'REFUND' | 'ADJUSTMENT'

/**
 * One ledger row.
 *
 * A `CHARGE` carries its full basis — all four token counters and the markup — because an
 * unexplainable charge is a chargeback, and the customer must be able to see how it was reached.
 */
export interface BalanceTransactionRow {
  id: string
  kind: BalanceTransactionKind
  /** Signed. A charge is negative. */
  amount_micros: number
  balance_after_micros: number
  currency: string
  model_id: string | null
  input_tokens: number | null
  output_tokens: number | null
  cache_write_tokens: number | null
  cache_read_tokens: number | null
  markup_bps: number | null
  description: string | null
  created_at: string
  /** A TOPUP's Stripe tax invoice and a REFUND's credit note (2026-10-TOPUP-TAX). Absent on older rows. */
  invoice_id?: string | null
  credit_note_id?: string | null
  /** Our own PDF path (`/billing/invoices/{id}/pdf` or `/billing/credit-notes/{id}/pdf`); fetched with auth, never a Stripe URL. */
  document_path?: string | null
}

/** One refund request as its admin sees it (`GET /api/billing/refund-requests`). */
export interface RefundRequest {
  id: string
  amount_micros: number
  amount_usd: number
  /** REQUESTED, APPROVED, PROCESSING, DONE, PENDING_MANUAL or REJECTED. */
  state: string
  reason: string | null
  decision_reason: string | null
  created_at: string
  decided_at: string | null
}

/** `GET /api/billing/refundable`: what a paid top-up can still give back, and what is held aside. */
export interface Refundable {
  refundable_micros: number
  refundable_usd: number
  reserved_by_requests_micros: number
  balance_micros: number
  requests: RefundRequest[]
}

/* -------------------------------------------------------------------------- */
/* Conversations (D23, D26)                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A direct thread, or a segment detected inside a channel.
 *
 * The console must tell them apart at a glance: a channel segment and a direct conversation are
 * different kinds of record, and rendering them identically would misrepresent both.
 */
export type ConversationKind = 'DIRECT' | 'CHANNEL'

/**
 * One row of the conversation rail.
 *
 * No message bodies — as with the old message index — but the important difference is that this is
 * one row per CONVERSATION rather than per message. Orders of magnitude fewer, which is why the
 * rail is paginated and uncapped where the message index had to be truncated.
 */
export interface ConversationSummary {
  id: string
  kind: ConversationKind
  started_at: string
  /** `null` while the conversation is still open. Render as ongoing, never as ending "now". */
  ended_at: string | null
  archived: boolean
  /** The channel name for a segment; `null` for a direct conversation. */
  label: string | null
  /** Names AS THEY WERE at the time. A participant renamed since still reads correctly here. */
  participants: string[]
  message_count: number
  anchor_seq: number | null
}

/**
 * `GET /api/zones/{zoneId}/conversations/index`.
 *
 * Note there is no `truncated` flag, unlike `ZoneMessageIndex`. That one had to admit it did not
 * reach the beginning; this one paginates, so `has_more` means "ask for the next page" rather than
 * "some rows are missing and you cannot have them".
 */
export interface ConversationIndex {
  conversations: ConversationSummary[]
  has_more: boolean
}

/** One matching message. `snippet` is highlighted server-side with `<<`/`>>`, not with HTML. */
export interface SearchHit {
  seq: number
  sender: string
  sent_at: string
  snippet: string
}

/** Matches within one conversation. Grouping is what makes a result set navigable. */
export interface SearchGroup {
  conversation_id: string
  kind: ConversationKind
  label: string | null
  participants: string[]
  match_count: number
  anchor_seq: number | null
  hits: SearchHit[]
}

/**
 * `GET /api/zones/{zoneId}/search?q=…`.
 *
 * `truncated` is reported rather than hidden: silently returning the first N matches of many is how
 * a user concludes something is not there.
 */
export interface SearchResults {
  total: number
  truncated: boolean
  groups: SearchGroup[]
}

/**
 * `POST /api/zones/{zoneId}/conversations/{id}/delete` body, and the zone/account equivalents.
 *
 * `acknowledged` is checked SERVER-SIDE. Sending it from a modal is not the control; the server
 * refusing without it is.
 */
export interface DeleteRequest {
  acknowledged: boolean
  reason?: string
}

/** What comes back from a queued deletion. `execute_after` is when it becomes irreversible. */
export interface DeletionRequested {
  id: string
  scope: string
  target_id: string | null
  requested_at: string
  execute_after: string
  state: string
}

/**
 * `POST /api/zones/{zoneId}/messages` body.
 *
 * Exactly one addressing mode: `to` (connection names) or `channel` (one
 * channel name). Requires a HUMAN presence in the zone — without one the
 * answer is 409 and the fix is `POST .../presence` first (§18).
 */
export interface SendMessageRequest {
  to?: string[]
  channel?: string
  message: string
}

/**
 * What happened to one addressed party (IMPROVEMENTS_1, WI3). The old `delivered` boolean meant
 * "queued" all along and could not say why nothing was written; this can.
 *
 *  - `queued` — a row was written for a live recipient; it sees it on its next read.
 *  - `queued_offline` — a row was written for a reserved identity that is offline. **Sent**, not
 *    undelivered: the queue survives a disconnect, so it waits for the identity's return.
 *  - `unknown` — no such party in this zone: nobody connected or reserved under that name, or a
 *    released one. Nothing written.
 *  - `failed` — refused for a stated reason (yourself, an empty name). Nothing written.
 */
export type DeliveryOutcome = 'queued' | 'queued_offline' | 'unknown' | 'failed'

/**
 * One addressed party in a send and what happened to it. Mirrors `messaging.DeliveryResult` — the
 * same record the MCP `say` and `say_channel` tools return (§6).
 */
export interface DeliveryResult {
  /** The requested name, echoed verbatim. */
  name: string
  /** The id of the row written for it, or null when nothing was written. */
  message_id: string | null
  outcome: DeliveryOutcome
  /** Human-readable reason when nothing was written, otherwise null. */
  error: string | null
}

/**
 * `POST /api/zones/{zoneId}/messages` response.
 *
 * **A 200 does not mean everyone heard you.** Naming three parties of which one is unknown is two
 * rows written and one reported failure inside a 200, not a 4xx (§6, and `MessageController`'s
 * "partial delivery is success"). The console must read `results` and say so.
 *
 * `results` lists every addressed party for a direct send and **every other member** for a
 * channel send (it used to be empty there). A channel send with zero results is a channel whose
 * only member is the sender — nothing was written. `delivered` counts rows written, `queued` and
 * `queued_offline` alike; it never counts rows read.
 */
export interface SendMessageResponse {
  delivered: number
  results: DeliveryResult[]
}

/* -------------------------------------------------------------------------- */
/* Police (§19)                                                               */
/* -------------------------------------------------------------------------- */

/**
 * A zone's police seat. `GET /api/zones/{zoneId}/police` and the response of
 * every write on that path — all five return this same shape.
 *
 * **There is no `api_key` field and there never will be.** §19: *"A GET that
 * echoes a customer's Claude key back into a browser is a leak whatever the
 * transport."* The server has no method that can return the plaintext, so the
 * console shows `api_key_prefix` plus `has_api_key` and nothing else. The form
 * field for it must therefore be write-only — never seeded from this object.
 */
export interface PoliceSettings {
  zone_id: string
  /** Whether this zone is supervised at all. Designating an agent forces it on. */
  enabled: boolean
  /** System-prefilled on first read, then user-editable. Max 8000 characters. */
  prompt: string
  model: string
  /** How hard the model thinks. The biggest cost lever after the model itself. */
  effort: PoliceEffort
  /**
   * `ASYNC` (default) analyses after the fact and never delays a message.
   * `GATED` evaluates every message before it is transmitted — slow, and priced
   * per message. The console warns in red.
   */
  supervision_mode: SupervisionMode
  has_api_key: boolean
  /** First 12 characters of the stored key, for recognition. Null when unset. */
  api_key_prefix: string | null
  api_key_set_at: string | null
  api_key_status: ApiKeyStatus
  api_key_checked_at: string | null
  /**
   * Derived server-side: true when a connected agent holds the seat rather than
   * saltseed. This is the single question the console's warning banner turns on.
   */
  delegated: boolean
  designated_connection_id: string | null
  /** Resolved for display. Null if the holder has since disconnected. */
  designated_connection_name: string | null
  designated_at: string | null
  /** Who clicked "I know what I am doing", stamped from the JWT — not the body. */
  acknowledged_by: string | null
  acknowledged_at: string | null
  created_at: string
  updated_at: string
}

/**
 * `PUT /api/zones/{zoneId}/police` body (zone administrator).
 *
 * Every field optional; **`null`/absent means "leave alone", including
 * `api_key`**. It cannot mean "clear", because the GET that populated the form
 * could not have contained the key, so an omitted field would wipe the
 * credential on every unrelated prompt edit. Clearing is the explicit
 * `DELETE .../police/api-key`.
 */
export interface UpdatePoliceRequest {
  enabled?: boolean
  prompt?: string
  api_key?: string
  model?: string
  effort?: PoliceEffort
  supervision_mode?: SupervisionMode
}

export type PoliceEffort = 'LOW' | 'MEDIUM' | 'HIGH'

export type SupervisionMode = 'ASYNC' | 'GATED'

/**
 * One selectable police model — `GET /api/zones/{zoneId}/police/models`.
 *
 * The server sends a cost PER EVALUATION rather than per million tokens, because
 * that is the question an administrator is actually answering.
 */
export interface PoliceModelOption {
  code: string
  display_name: string
  model_id: string
  input_price_micros_per_mtok: number
  output_price_micros_per_mtok: number
  cache_write_price_micros_per_mtok: number
  cache_read_price_micros_per_mtok: number
  markup_bps: number
  estimated_micros_per_evaluation: number
  /**
   * True for models unavailable under zero data retention. Shown as a warning
   * BEFORE the model is saved — a rejected request found in production looks
   * like an outage, and the cause is a setting nobody on that screen can see.
   */
  requires_data_retention: boolean
}

/**
 * `POST /api/zones/{zoneId}/police/designate` body.
 *
 * `acknowledged` must be literally `true` — the server answers 400 otherwise
 * (§19: *"the modal is a UI affordance and UI is not a control"*), and stamps
 * who acknowledged from the authenticated caller. Never send `true` from
 * anywhere but the confirmation modal.
 */
export interface DesignatePoliceRequest {
  connection_id: string
  acknowledged: true
}

/* -------------------------------------------------------------------------- */
/* Add-ons (§19)                                                              */
/* -------------------------------------------------------------------------- */

/**
 * One row of `GET /api/account/addons` — **every add-on in the catalogue**,
 * enrolled or not, so the console can offer something the account does not have
 * yet. `enabled_at` is stamped on the first enable and left alone by a repeat,
 * so it keeps meaning "since when"; disabling keeps the row, because the row is
 * the billing evidence.
 */
export interface AccountAddon {
  /** `police` is the only one today. Extensible, so typed as a string. */
  code: string
  name: string
  description: string
  enabled: boolean
  enabled_at: string | null
  enabled_by: string | null
}

/** `PUT /api/account/addons/{code}` body (ADMIN). 403 for a plain USER. */
export interface UpdateAddonRequest {
  enabled: boolean
}

/** The add-on code §19 names. Policing is a paid add-on. */
export const POLICE_ADDON = 'police'

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The `type` URIs `ApiExceptionHandler` stamps on a problem, for the cases the
 * SPA has to tell apart.
 *
 * The status code alone is not enough: the server answers **409** for at least
 * six unrelated conditions — a missing HUMAN presence, a taken name, a quota,
 * an archived row, the last administrator, a connection that is not connected —
 * and only the `type` distinguishes them. Discriminate on this, never on `409`.
 */
export const PROBLEM_TYPES = {
  presenceRequired: 'https://saltseed.com/problems/presence-required',
  conflict: 'https://saltseed.com/problems/conflict',
  limitExceeded: 'https://saltseed.com/problems/limit-exceeded',
  administratorOnly: 'https://saltseed.com/problems/administrator-only',
  notFound: 'https://saltseed.com/problems/not-found',
  /**
   * §19's third authority, 403. Distinct from `administratorOnly`, which is the
   * account role: a plain USER can hold this one for the zones they administer,
   * and an account ADMIN holds it for every zone of their account.
   */
  zoneAdministratorOnly: 'https://saltseed.com/problems/zone-administrator-only',
  /** 400 from `POST .../police/designate` without `acknowledged: true`. */
  acknowledgementRequired: 'https://saltseed.com/problems/acknowledgement-required',
} as const

/** RFC 7807 problem detail, Spring's default error shape for `/api/**`. */
export interface ProblemDetail {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  /** Spring's legacy `{timestamp, status, error, message, path}` shape. */
  message?: string
  error?: string
  /** Machine-readable refusal code of the staff API (`mfa_required`, `reason_required`, ...). */
  code?: string
  /** Quota refusals (`type` ends `/limit-exceeded`): the cap that was hit. */
  limit?: number
  /** Quota refusals: the cheapest sellable plan code that lifts the cap, or null. */
  upgrade_plan?: string | null
  /** `refund_exceeds_refundable`: what is refundable now, in micros. */
  refundable_micros?: number
  /** `payment_declined`: the open invoice the console offers to pay with another card. */
  invoice_id?: string
}

/* -------------------------------------------------------------------------- */
/* Skill bundles                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The agent runtimes saltseed ships a skill bundle for.
 *
 * These are the `{vendor}` segment of
 * `GET /api/zones/{zoneId}/tooling/skills/{vendor}.zip`. Anything else is a 404,
 * so this union must stay in step with the backend `SkillFlavor` enum.
 */
export type SkillVendor = 'claude' | 'openai' | 'cursor' | 'gemini'

/**
 * What the model API said last time saltseed tried the stored key (§21).
 *
 * There is deliberately no `INVALID`: verification can only ask api.anthropic.com,
 * so a gateway or router token is refused exactly like a truncated one. `REJECTED`
 * records what happened; the console supplies the caveat.
 */
export type ApiKeyStatus = 'UNVERIFIED' | 'VERIFIED' | 'REJECTED' | 'UNREACHABLE'

/* -------------------------------------------------------------------------- */
/* Server tooling (IMPROVEMENTS_2 P1, CB-WI9)                                 */
/* -------------------------------------------------------------------------- */

/**
 * `GET /tooling/info` — `BridgeController`, public, deliberately **not** under `/api`. What this
 * saltseed server speaks and what bridge (if any) it ships.
 *
 * `bridge_version` and `bridge_sha256` are both null together, never one without the other: a
 * build that packed no tarball into the image has nothing to publish a digest for. The console
 * reads that pair as "no bridge available from this server" rather than an error.
 */
export interface ToolingInfo {
  protocol_version: number
  bridge_version: string | null
  bridge_sha256: string | null
  /** Protocol 7: the `saltseed` helper this build ships; null/absent on a build without one. */
  helper_version?: string | null
  /** `os-arch -> sha256` of each helper binary. */
  helper_sha256?: Record<string, string>
}

/* --- feedback to saltseed staff ------------------------------------------- */

export type FeedbackCategory =
  | 'bug'
  | 'cost'
  | 'protocol'
  | 'installer'
  | 'ux'
  | 'feature'
  | 'other'

export type FeedbackTriageState = 'new' | 'triaged' | 'actioned' | 'dismissed'

/** `POST /api/feedback` body. Console submissions never carry an attachment. */
export interface SubmitFeedbackRequest {
  category: FeedbackCategory
  title?: string
  body: string
  zone_id?: string
}

/** `POST /api/feedback` -> 200. `redactions` counts secrets scrubbed from the body. */
export interface SubmitFeedbackResponse {
  feedback_id: string
  received: boolean
  redactions: number
}

export interface FeedbackAttachment {
  handle: string
  filename: string
  size_bytes: number
}

/** One report, as the staff API returns it. List items have `body: null`. */
export interface StaffFeedbackItem {
  feedback_id: string
  submitted_at: string
  source: 'agent' | 'console'
  category: FeedbackCategory
  title: string | null
  body: string | null
  redactions: number
  user_email: string | null
  account_name: string | null
  zone_id: string | null
  zone_name: string | null
  identity_name: string | null
  client_name: string | null
  client_version: string | null
  protocol_version: string | null
  server_version: string | null
  helper_version: string | null
  counters: Record<string, unknown> | null
  attachment: FeedbackAttachment | null
  triage_state: FeedbackTriageState
  triaged_by: string | null
  triaged_at: string | null
  staff_note: string | null
}

/** `GET /api/staff/feedback` — keyset-paged on `cursor`. */
export interface StaffFeedbackPage {
  items: StaffFeedbackItem[]
  next_cursor: string | null
  has_more: boolean
}

/** `PATCH /api/staff/feedback/{id}` — either field may be omitted. */
export interface TriageFeedbackRequest {
  triage_state?: FeedbackTriageState
  staff_note?: string
}

/* -------------------------------------------------------------------------- */
/* Billing (stripe.md §6-§9): the PLAN, as opposed to the police balance above */
/* -------------------------------------------------------------------------- */

export interface QuotaUsage {
  in_use: number
  limit: number
  over: boolean
}

export interface OverLimitZone {
  zone_id: string
  name: string
}

/** Present while the account is above its plan's caps and inside the 14-day grace. */
export interface OverLimit {
  since: string
  deadline: string
  zones: QuotaUsage | null
  connections_per_zone: QuotaUsage | null
  channels_per_zone: QuotaUsage | null
  members: QuotaUsage | null
  would_deactivate: OverLimitZone[]
  keep_zone_ids: string[]
}

/** `GET /api/billing` (ADMIN). */
export interface BillingSummary {
  plan_code: string
  plan_name: string
  /** `stripe` or `staff` (a comp) -- lower-case. */
  source: string | null
  /** Stripe's lower-case status, or `none`. */
  status: string
  interval: 'month' | 'year' | null
  extra_seats: number
  current_period_end: string | null
  cancel_at_period_end: boolean
  over_limit_since: string | null
  over_limit_deadline: string | null
  has_customer: boolean
  billing_enabled: boolean
  over_limit: OverLimit | null
}

/** `POST /api/billing/checkout` and `/topup`: an Embedded Checkout session to mount (BI2). */
export interface CheckoutSession {
  client_secret: string
  session_id: string
}

/**
 * `POST /api/billing/checkout` (bug-224). `subscribed`: created server-side on the default card, no
 * Checkout; the plan lands with the webhook. `checkout`: mount `client_secret` in Embedded Checkout.
 */
export interface SubscribeResult {
  outcome: 'subscribed' | 'checkout'
  client_secret?: string
  session_id?: string
  subscription_id?: string
}

/** `GET /api/billing/config`. */
export interface BillingConfig {
  publishable_key: string | null
  billing_enabled: boolean
}

export type InvoiceKind = 'subscription' | 'topup' | 'recharge' | 'other'

export interface InvoiceLine {
  description: string | null
  amount_cents: number
  quantity: number | null
}

export interface CreditNote {
  id: string
  number: string | null
  created: string
  total_cents: number
  reason: string | null
}

export interface Invoice {
  id: string
  number: string | null
  created: string
  kind: InvoiceKind
  /** Stripe's lower-case status: draft, open, paid, void, uncollectible. */
  status: string
  subtotal_cents: number
  tax_cents: number
  total_cents: number
  amount_paid_cents: number
  amount_due_cents: number
  amount_refunded_cents: number
  currency: string
  period_start: string | null
  period_end: string | null
  lines: InvoiceLine[]
  credit_notes: CreditNote[]
}

export interface InvoicePage {
  invoices: Invoice[]
  next_cursor: string | null
  has_more: boolean
}

export interface InvoiceAmount {
  amount_due_cents: number
  subtotal_cents: number
  tax_cents: number
  total_cents: number
  currency: string
  date: string | null
}

export interface UpcomingInvoice {
  has_subscription: boolean
  next_invoice: InvoiceAmount | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  scheduled_change: { plan_code: string; interval: BillingInterval; effective_at: string | null } | null
}

type BillingInterval = 'month' | 'year'

export interface PaymentMethodCard {
  id: string
  brand: string
  last4: string
  exp_month: number
  exp_year: number
  is_default: boolean
  created: string | null
}

export interface BillingAddress {
  line1: string | null
  line2: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country: string | null
}

export interface BillingDetails {
  email?: string | null
  name: string | null
  address: BillingAddress
}

export interface TaxId {
  id: string
  type: string
  value: string
  country: string | null
  verification: string | null
}

/** `keep` (bug-223): the plan in force chosen again while a downgrade is scheduled; nothing is charged. */
export type PlanChangeDirection = 'upgrade' | 'downgrade' | 'keep' | string

export interface PlanChangePreview {
  direction: PlanChangeDirection
  plan_code: string
  interval: BillingInterval
  /** Upgrades only: the prorated charge today, in cents. */
  due_now: InvoiceAmount | null
  effective_at: string | null
  next_renewal: InvoiceAmount | null
}

export interface PlanChangeResult {
  outcome: 'applied' | 'payment_required' | 'scheduled' | 'kept'
  direction: PlanChangeDirection
  plan_code: string
  interval: BillingInterval
  effective_at: string | null
  invoice_id: string | null
}

export interface SubscriptionState {
  status: string
  cancel_at_period_end: boolean
  current_period_end: string | null
}

/** `GET/PUT /api/billing/police-recharge`. Amounts are dollars. */
export interface PoliceRecharge {
  enabled: boolean
  active: boolean
  card_saved: boolean
  threshold_usd: number
  amount_usd: number
  minimum_amount_usd: number
  max_per_day: number
}
